import { HighlightText } from '@/components/treeSelector/components/HighlightText';
import {
  IconMap,
  SelectorCheckable,
  SelectorItem,
} from '@/components/treeSelector/components/SelectorItem';
import { FlatTreeNode } from '@/components/treeSelector/types/tree';
import { cn } from '@/lib/utils';
import { ChevronRight } from 'lucide-react';
import { ReactNode } from 'react';

// Generic property component that can be used by any variant

export const Property = <TData extends string, TPayload = unknown>({
  flatNode,
  missingLabelText,
  iconMap,
  nodeData,
  toggleExpansion,
  searchTerm,
  renderCustomNode,
}: {
  flatNode: FlatTreeNode<TData, TPayload>;
  missingLabelText: string;
  iconMap: IconMap<TData>;
  nodeData: { checkedState: boolean | 'indeterminate'; toggleNode: () => void } | null;
  toggleExpansion: (nodeId: string) => void;
  searchTerm: string;
  renderCustomNode?: (flatNode: FlatTreeNode<TData, TPayload>, label: ReactNode) => ReactNode;
}) => {
  const { node, level, isExpanded } = flatNode;

  if (!nodeData) return null;

  const { checkedState, toggleNode } = nodeData;

  const Label = (
    <HighlightText searchTerm={searchTerm}>{node.label ?? missingLabelText}</HighlightText>
  );

  // Allow custom rendering for special node types (e.g., METER in sourcing)
  if (renderCustomNode) {
    const customRender = renderCustomNode(flatNode, Label);
    if (customRender) return <>{customRender}</>;
  }

  return (
    <div style={{ paddingLeft: `${level * 27}px` }}>
      <SelectorCheckable
        checked={checkedState}
        onClick={toggleNode}
        aria-label={`${node.type} ${node.label}`}
      >
        <button
          className="flex w-full cursor-pointer items-center gap-x-1"
          onClick={() => toggleExpansion(node.id)}
        >
          <ChevronRight className={cn('size-5 shrink-0', { 'rotate-90': isExpanded })} />
          <SelectorItem type={flatNode.type} iconMap={iconMap}>
            {Label}
          </SelectorItem>
        </button>
      </SelectorCheckable>
    </div>
  );
};
